import React, { useEffect, useMemo, useState } from "react";

// mui components
import {
	Box,
	Chip,
	Divider,
	IconButton,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";

// mui icons
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";

// custom components
import Heading3 from "../../../typography/heading-03";
import Heading5 from "../../../typography/heading-05";
import Body2 from "../../../typography/body-02";
import SecondaryButton from "../../../buttons/secondary-button";
import LoadingDataModal from "../../../modal/loading-data-modal";

// api
import { useGetGlossaryConfigMutation } from "../../../../features/glossary-config/getGlossaryConfigApiSlice";

// custom styles
import styles from "./index.module.scss";

const normalizeText = (value) => String(value ?? "").trim();

const firstNonEmpty = (...values) => {
	for (const value of values) {
		if (Array.isArray(value)) {
			const joined = value.filter(Boolean).join(", ").trim();
			if (joined) return joined;
			continue;
		}

		const text = normalizeText(value);
		if (text) return text;
	}

	return "";
};

const normalizeGlossaryRow = (row) => ({
	category: firstNonEmpty(row?.category, row?.group, "General"),
	term: firstNonEmpty(row?.term, row?.title, row?.label, "Untitled Term"),
	description: firstNonEmpty(
		row?.description,
		row?.definition,
		row?.body,
		row?.value,
		""
	),
});

const getGlossaryEntries = (value) => {
	if (!value) return [];
	if (Array.isArray(value)) return value;
	for (const key of [
		"rows",
		"data",
		"results",
		"items",
		"glossaryRows",
		"glossarySections",
		"glossaryConfig",
	]) {
		const candidate = value?.[key];

		if (Array.isArray(candidate)) return candidate;
		if (candidate && typeof candidate === "object") {
			return getGlossaryEntries(candidate);
		}
	}

	return [value];
};

const normalizeGlossaryRows = (value) => {
	const source = getGlossaryEntries(value);

	return source
		.flatMap((item) => {
			if (!item) return [];

			if (Array.isArray(item?.items)) {
				return item.items.map((entry) =>
					normalizeGlossaryRow({
						...entry,
						category: entry?.category ?? item?.category,
					})
				);
			}

			return [normalizeGlossaryRow(item)];
		})
		.filter((row) => row.term || row.description);
};

const groupGlossaryRows = (rows) =>
	rows.reduce((sections, row) => {
		const category = row?.category || "General";
		const existingSection = sections.find(
			(section) => section.category === category
		);

		if (existingSection) {
			existingSection.items.push(row);
			return sections;
		}

		sections.push({
			category,
			items: [row],
		});

		return sections;
	}, []);

const GlossaryDrawer = ({ handleCloseDrawer }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [
		getGlossaryConfig,
		{
			data: glossaryConfigData,
			isLoading: isGlossaryLoading,
			error: glossaryLoadError,
		},
	] = useGetGlossaryConfigMutation();

	useEffect(() => {
		void getGlossaryConfig();
	}, [getGlossaryConfig]);

	const resolvedGlossaryRows = useMemo(() => {
		return normalizeGlossaryRows(glossaryConfigData);
	}, [glossaryConfigData]);

	const filteredRows = useMemo(() => {
		const query = searchQuery.toLowerCase().trim();

		if (!query) return resolvedGlossaryRows;

		return resolvedGlossaryRows.filter((row) =>
			[row?.category, row?.term, row?.description]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(query))
		);
	}, [resolvedGlossaryRows, searchQuery]);

	const glossarySections = useMemo(
		() => groupGlossaryRows(filteredRows),
		[filteredRows]
	);

	const totalGlossaryTerms = resolvedGlossaryRows.length;
	const visibleGlossaryTerms = filteredRows.length;
	const hasSearchQuery = Boolean(searchQuery.trim());
	const hasResults = glossarySections.length > 0;
	const showLoadingState =
		isGlossaryLoading && resolvedGlossaryRows.length === 0;

		return (
		<>
			<LoadingDataModal
				open={showLoadingState}
				text="Loading glossary terms..."
			/>
			<Stack direction="column" className={styles.stackContainer}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					className={styles.drawerHeaderContainer}
				>
					<Heading3
						text="Glossary"
						extraClass={styles.headerText}
						fontType="semibold"
					/>
					<SecondaryButton
						startIcon={<CloseIcon />}
						text="Close"
						onClick={handleCloseDrawer}
					/>
				</Stack>

				<Stack direction="column" className={styles.drawerBodyContainer}>
					<Box className={styles.searchPanel}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							className={styles.searchPanelHeader}
						>
							<Stack direction="column" className={styles.searchCopy}>
								<Body2
									text="A quick reference for tracker terminology."
									extraClass={styles.subtitleText}
								/>
								<Stack
									direction="row"
									alignItems="center"
									className={styles.metaRow}
								>
									<Chip
										label={`${totalGlossaryTerms} terms`}
										size="small"
										className={styles.metaChip}
									/>
									<Body2
										text={
											hasSearchQuery
												? `${visibleGlossaryTerms} matching results`
												: "Browse categories below"
										}
										extraClass={styles.metaText}
									/>
								</Stack>
							</Stack>
						</Stack>

						<Divider className={styles.searchDivider} />

						<TextField
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
							placeholder="Search glossary"
							fullWidth
							size="small"
							className={styles.searchField}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchOutlinedIcon className={styles.searchIcon} />
									</InputAdornment>
								),
								endAdornment: hasSearchQuery ? (
									<InputAdornment position="end">
										<Tooltip title="Clear search">
											<IconButton
												size="small"
												className={styles.inlineClearButton}
												onClick={() => setSearchQuery("")}
												aria-label="Clear search"
											>
												<ClearRoundedIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</InputAdornment>
								) : null,
							}}
						/>
					</Box>

					{showLoadingState ? null : hasResults ? (
						<Stack direction="column" className={styles.sectionsContainer}>
							{glossarySections.map((section) => (
								<Stack
									key={section.category}
									direction="column"
									className={styles.categorySection}
								>
									<Heading5
										text={section.category}
										fontType="semibold"
										extraClass={styles.categoryHeading}
									/>

									<TableContainer className={styles.tableContainer}>
										<Table
											sx={{ tableLayout: "fixed", width: "100%" }}
											aria-label={section.category}
										>
											<TableHead>
												<TableRow>
													<TableCell sx={{ width: "28%", verticalAlign: "top" }}>
														<Heading5
															text="Term"
															fontType="semibold"
															extraClass={styles.thText}
														/>
													</TableCell>
													<TableCell sx={{ width: "72%", verticalAlign: "top" }}>
														<Heading5
															text="Description"
															fontType="semibold"
															extraClass={styles.thText}
														/>
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{section.items.map((row) => (
													<TableRow key={`${row.category}-${row.term}`}>
														<TableCell sx={{ verticalAlign: "top" }}>
															<Body2
																text={row.term}
																extraClass={styles.greenText}
															/>
														</TableCell>
														<TableCell sx={{ verticalAlign: "top" }}>
															<Body2
																text={row.description || "NA"}
																extraClass={styles.bodyText}
															/>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Stack>
							))}
						</Stack>
					) : (
						<Box className={styles.emptyState}>
							<Body2
								text={
									glossaryLoadError
										? "Unable to load glossary terms"
										: searchQuery.trim()
											? "No glossary terms found"
											: "No glossary terms configured"
								}
								extraClass={styles.emptyStateText}
							/>
						</Box>
					)}
				</Stack>
			</Stack>
		</>
	);
};

export default GlossaryDrawer;
